import React, { useState } from 'react';
import { Copy, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { Button, DataTable, Modal, MonoCell, type DataTableColumn } from '@/components/ds-zta';
import { copyWithToast } from '@/lib/copy';

type DetailRow = { label: string; value: string; mono: boolean };

const DETAIL_COLUMNS: DataTableColumn<DetailRow>[] = [
    {
        key: 'label',
        header: <span className="sr-only">Field</span>,
        cell: (row) => <span className="text-[var(--d2d-text-secondary)] font-medium">{row.label}</span>,
    },
    {
        key: 'value',
        header: <span className="sr-only">Value</span>,
        cell: (row) => (
            <span className="text-[var(--d2d-text-primary)]">
                {row.mono ? <MonoCell value={row.value} /> : row.value}
            </span>
        ),
    },
];

interface ConnectionInfo {
    status: string;
    access_token: string;
    expires_at: string;
    instructions: {
        step_1: {
            command: string;
            description: string;
        };
        step_2: {
            command: string;
            description: string;
        };
    };
    connection_details: {
        host: string;
        port: string;
        protocol: string;
        database: string;
    };
    notes: string[];
}

interface DatabaseConnectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    connectionInfo: ConnectionInfo | null;
    resourceName: string;
}

export const DatabaseConnectionModal: React.FC<DatabaseConnectionModalProps> = ({
    isOpen,
    onClose,
    connectionInfo,
    resourceName,
}) => {
    const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

    if (!isOpen || !connectionInfo) return null;

    const copyToClipboard = async (text: string, label: string) => {
        if (!(await copyWithToast(text, { success: false }))) return;
        setCopiedCommand(label);
        setTimeout(() => setCopiedCommand(null), 2000);
    };

    const getTimeRemaining = () => {
        const expiresAt = new Date(connectionInfo.expires_at);
        const now = new Date();
        const diffMs = expiresAt.getTime() - now.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        return diffMins > 0 ? diffMins : 0;
    };

    const timeRemaining = getTimeRemaining();

    return (
        <Modal
            open
            onClose={onClose}
            title={<>Connect to {resourceName}</>}
            size="lg"
            footer={<Button variant="primary" onClick={onClose}>Close</Button>}
        >
                    {/* Body */}
                    <div className="space-y-6">


                        {/* Instructions */}
                        <div className="bg-[var(--zta-tint-info)] border border-[var(--zta-tint-info-border)] rounded-md p-4">
                            <div className="flex items-start">
                                <AlertCircle className="h-5 w-5 text-[var(--d2d-info)] mt-0.5 mr-3 flex-shrink-0" />
                                <p className="text-sm text-[var(--d2d-info)]">
                                    Run these commands in your terminal to create a secure tunnel to the database:
                                </p>
                            </div>
                        </div>

                        {/* Step 1: Auth */}
                        <div>
                            <label className="block text-sm font-medium text-[var(--d2d-text-secondary)] mb-2">
                                Step 1: Authenticate
                            </label>
                            <p className="text-xs text-[var(--d2d-text-muted)] mb-2">
                                {connectionInfo.instructions.step_1.description}
                            </p>
                            <div className="relative">
                                <pre className="border border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)] text-[var(--d2d-text-primary)] p-3 pr-24 rounded-md overflow-x-auto text-xs font-mono">
                                    {connectionInfo.instructions.step_1.command}
                                </pre>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="absolute top-2 right-2"
                                    onClick={() => copyToClipboard(connectionInfo.instructions.step_1.command, 'step1')}
                                    leadingIcon={copiedCommand === 'step1' ? <CheckCircle className="h-4 w-4 text-[var(--d2d-success)]" /> : <Copy className="h-4 w-4" />}
                                >
                                    {copiedCommand === 'step1' ? 'Copied' : 'Copy'}
                                </Button>
                            </div>
                        </div>

                        {/* Step 2: Connect */}
                        <div>
                            <label className="block text-sm font-medium text-[var(--d2d-text-secondary)] mb-2">
                                Step 2: Connect to Database
                            </label>
                            <p className="text-xs text-[var(--d2d-text-muted)] mb-2">
                                {connectionInfo.instructions.step_2.description}
                            </p>
                            <div className="relative">
                                <pre className="border border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)] text-[var(--d2d-text-primary)] p-3 pr-24 rounded-md overflow-x-auto text-xs font-mono">
                                    {connectionInfo.instructions.step_2.command}
                                </pre>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="absolute top-2 right-2"
                                    onClick={() => copyToClipboard(connectionInfo.instructions.step_2.command, 'step2')}
                                    leadingIcon={copiedCommand === 'step2' ? <CheckCircle className="h-4 w-4 text-[var(--d2d-success)]" /> : <Copy className="h-4 w-4" />}
                                >
                                    {copiedCommand === 'step2' ? 'Copied' : 'Copy'}
                                </Button>
                            </div>
                        </div>

                        {/* Connection Details */}
                        <div>
                            <label className="block text-sm font-medium text-[var(--d2d-text-secondary)] mb-2">
                                Connection Details
                            </label>
                            <div className="bg-[var(--d2d-bg-primary)] rounded-md p-4">
                                <DataTable
                                    aria-label="Connection details"
                                    columns={DETAIL_COLUMNS}
                                    rowKey={(row) => row.label}
                                    rows={[
                                        { label: 'Host:', value: connectionInfo.connection_details.host, mono: true },
                                        { label: 'Port:', value: connectionInfo.connection_details.port, mono: true },
                                        { label: 'Protocol:', value: connectionInfo.connection_details.protocol, mono: false },
                                        { label: 'Database:', value: connectionInfo.connection_details.database, mono: true },
                                    ]}
                                />
                            </div>
                        </div>

                        {/* Notes */}
                        {connectionInfo.notes && connectionInfo.notes.length > 0 && (
                            <div className="bg-[var(--d2d-bg-primary)] rounded-md p-4">
                                <p className="text-xs font-medium text-[var(--d2d-text-secondary)] mb-2">Important Notes:</p>
                                <ul className="text-xs text-[var(--d2d-text-secondary)] space-y-1 list-disc list-inside">
                                    {connectionInfo.notes.map((note, idx) => (
                                        <li key={idx}>{note}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

        </Modal>
    );
};
